import { useState, useRef, ChangeEvent, useEffect } from 'react'
import { profileService } from '@/services/profile'
import { useAuth } from '@/context/auth-context'

interface AvatarUploadProps {
  onUploadSuccess?: (url: string) => void
  onUploadError?: (error: string) => void
}

function AvatarUpload({ onUploadSuccess, onUploadError }: AvatarUploadProps) {
  const [isUploading, setIsUploading] = useState(false)
  const [previewUrl, setPreviewUrl] = useState<string | null>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [forceRefresh, setForceRefresh] = useState(0)

  // Use Auth Context instead of Redux
  const { user, profile, updateProfile, refreshSession } = useAuth()

  // Add cache-busting timestamp to avatar URL
  const addCacheBuster = (url: string | null | undefined) => {
    if (!url) return null
    const timestamp = forceRefresh || new Date().getTime()
    return `${url}?t=${timestamp}`
  }

  const currentAvatarUrl =
    addCacheBuster(profile?.avatar_url) ||
    'https://www.gravatar.com/avatar/00000000000000000000000000000000?d=mp&f=y'

  function handleFileChange(event: ChangeEvent<HTMLInputElement>) {
    const file = event.target.files?.[0]
    if (!file) return

    // Check file type
    if (!file.type.startsWith('image/')) {
      const errorMsg = 'Please select an image file'
      onUploadError?.(errorMsg)
      return
    }

    // Check file size (max 2MB)
    if (file.size > 2 * 1024 * 1024) {
      const errorMsg = 'Image size should be less than 2MB'
      onUploadError?.(errorMsg)
      return
    }

    // Create a preview URL
    const objectUrl = URL.createObjectURL(file)
    setPreviewUrl(objectUrl)

    // Upload the file
    handleUpload(file)

    // Clean up the preview URL when component unmounts
    return () => URL.revokeObjectURL(objectUrl)
  }

  async function handleUpload(file: File) {
    if (!user?.id) {
      return
    }

    try {
      setIsUploading(true)

      const result = await profileService.uploadAvatar(user.id, file)

      if (result.error) {
        onUploadError?.(result.error)
        return
      }

      if (result.data?.path) {
        // Update the profile using Auth Context
        const { error } = await updateProfile({ avatar_url: result.data.path })

        if (error) {
          onUploadError?.(error)
          return
        }

        // Force a refresh of the avatar image by updating timestamp and refreshing session
        const newTimestamp = new Date().getTime()
        setForceRefresh(newTimestamp)

        // Refresh the session to ensure the latest avatar is loaded
        try {
          await refreshSession()
        } catch (refreshError) {
          // Non-critical error, can be ignored
        }

        onUploadSuccess?.(result.data.path)
      }
    } catch (error) {
      const errorMsg = error instanceof Error ? error.message : 'Failed to upload avatar'
      onUploadError?.(errorMsg)
    } finally {
      setIsUploading(false)
    }
  }

  function handleButtonClick() {
    fileInputRef.current?.click()
  }

  return (
    <div className="flex flex-col items-center space-y-4">
      <div className="relative group">
        <div className="w-32 h-32 rounded-full overflow-hidden border-2 border-primary">
          <img
            src={previewUrl || currentAvatarUrl}
            alt="Profile avatar"
            className="w-full h-full object-cover"
          />
        </div>

        <div
          className="absolute inset-0 flex items-center justify-center bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity rounded-full cursor-pointer"
          onClick={handleButtonClick}
        >
          <span className="text-white text-sm font-medium">
            {isUploading ? 'Uploading...' : 'Change Avatar'}
          </span>
        </div>
      </div>

      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={handleFileChange}
        disabled={isUploading}
      />

      <button
        type="button"
        onClick={handleButtonClick}
        disabled={isUploading}
        className="py-1 px-3 text-sm bg-primary hover:bg-primary-dark text-white font-medium rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
      >
        {isUploading ? 'Uploading...' : 'Upload New Avatar'}
      </button>
    </div>
  )
}

export { AvatarUpload }
